/**
 * First-login tour state: has this person seen it, should it auto-start, and how
 * the "?" menu replays it.
 *
 * WHY LOCALSTORAGE AND NOT A COLUMN
 * ---------------------------------
 * "Seen the tour" wants to live on `users`, and it does not: this workstream has
 * a single migration owner (`knowledge/client-support/plans/worker-rules.md`),
 * and Phase 3 was specified as no-schema work. So the flag is per-browser, keyed
 * by user id, and the failure mode is chosen deliberately:
 *
 *   • same person, second browser  → tour offers itself again (mild)
 *   • same person, cleared storage → tour offers itself again (mild)
 *
 * versus the alternative failure of a server flag we cannot write: a client who
 * dismisses it and gets it again on every page load. Re-showing a dismissable
 * 7-step tour on a new machine is the cheap error; that is the trade being made,
 * and it is the reason a `users.launch_tour_seen_at` column is worth adding the
 * next time this workstream opens a revision.
 *
 * Keyed by user id, not a bare key, because a shared office desktop where two
 * people at the same carrier sign in one after the other is a real setup —
 * the second person has not seen the tour just because the first one did.
 */
import { ref } from 'vue'

/** Window event the shell's "?" menu fires to replay the tour. */
export const LAUNCH_TOUR_EVENT = 'cs:launch-tour-start'

const STORAGE_PREFIX = 'cs.launchTourSeen.'

/**
 * The header and dashboard Action Center both need to be mounted before the
 * first card opens. The app may still be on /login when auth first changes.
 */
const SHELL_SELECTOR = '[data-tour-chrome]'
const DASHBOARD_SELECTOR = '[data-tour="action-center"]'
const SHELL_POLL_MS = 100
const SHELL_TIMEOUT_MS = 8000

function key(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`
}

/** Safe against Safari private mode / storage-disabled, where every access throws. */
function readSeen(userId: string): boolean {
  try {
    return localStorage.getItem(key(userId)) !== null
  } catch {
    // Storage unavailable: treat as "already seen" so a client can never be
    // trapped in a tour that reopens on every navigation.
    return true
  }
}

function writeSeen(userId: string): void {
  try {
    localStorage.setItem(key(userId), new Date().toISOString())
  } catch {
    // Nothing to do — the tour is dismissable for this session either way.
  }
}

/**
 * Resolve once the dashboard shell and its Action Center are on screen, or give up.
 *
 * The app root mounts BEFORE the layout does — every portal route is a lazy
 * `import()`, so on a cold first load the header is 600–1500ms behind
 * `onMounted`. Starting the tour into that gap is not a cosmetic problem: the
 * engine measures its park offset once, 450ms after the step opens, and never
 * again, so a card that starts before the header exists sits ON TOP of the
 * header for the whole step. (Measured, 2026-08-12: the anchor count is 0 at
 * t+600ms and 2 at t+1500ms.)
 *
 * Waiting also happens to be the right product behaviour on its own terms — a
 * welcome tour that opens over a blank loading screen introduces nothing.
 *
 * Times out rather than waiting forever: if the dashboard never appears, the
 * next login can try again without having spent the seen flag.
 */
function whenDashboardReady(isCancelled: () => boolean): Promise<boolean> {
  const ready = () =>
    !!document.querySelector(SHELL_SELECTOR) &&
    !!document.querySelector(DASHBOARD_SELECTOR)
  if (isCancelled()) return Promise.resolve(false)
  if (ready()) return Promise.resolve(true)
  return new Promise((resolve) => {
    const started = Date.now()
    const timer = setInterval(() => {
      if (isCancelled()) {
        clearInterval(timer)
        resolve(false)
      } else if (ready()) {
        clearInterval(timer)
        resolve(true)
      } else if (Date.now() - started > SHELL_TIMEOUT_MS) {
        clearInterval(timer)
        resolve(false)
      }
    }, SHELL_POLL_MS)
  })
}

export function useLaunchTour() {
  const active = ref(false)
  let requestGeneration = 0

  /** Invalidate an in-flight wait when the authenticated session changes. */
  function cancelPending(): void {
    requestGeneration += 1
    active.value = false
  }

  /**
   * Auto-start on a first login. Callers gate on the REAL token's role before
   * calling: a staff member in view-as is not the carrier, and a demo is not a
   * first login.
   */
  async function maybeAutoStart(userId: string | null | undefined): Promise<void> {
    if (!userId) return
    if (readSeen(userId)) return
    const generation = ++requestGeneration
    if (!(await whenDashboardReady(() => generation !== requestGeneration))) return
    if (generation !== requestGeneration) return
    // A client who closes the tour has still seen it. Waiting for the dashboard
    // before writing this flag lets a failed or cancelled mount retry next time.
    writeSeen(userId)
    active.value = true
  }

  /** The "?" menu's Replay — always runs, seen-flag or not. */
  function start(): void {
    active.value = true
  }

  return { active, maybeAutoStart, cancelPending, start }
}
